import {Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Device} from '../../../model/device';
import {DeviceMeasurement} from '../../../model/device-measurement';
import {DeviceProperty} from '../../../model/device-property';
import {IntegrationModule} from '../../../model/integration-module';
import {Asset} from '../../../model/asset';
import {AssetSearchData} from '../../../model/asset-search-data';
import {DevicePersistRequest} from '../../../model/requests/device-persist-request';
import {DevicesService} from '../../../services/devices.service';
import {ActivatedRoute, Router} from '@angular/router';
import {CommonMessagesService} from '../../../services/common-messages.service';
import {MapService} from '../../../services/map.service';
import * as mapboxgl from 'mapbox-gl';
import {ComponentCanDeactivate} from '../../../common/can-deactivate/component-can-deactivate';
import {DevicesFormInfoComponent} from './devices-form-info/devices-form-info.component';
import {HttpStatusCode} from '@angular/common/http';
import {TemplatesService} from "../../../services/templates.service";
import {Template} from "../../../model/template";
import {DefaultDeviceCatalog} from "../../../model/default-device-catalog";
import {HwCatalogService} from "../../../services/hw-catalog.service";
import {DefaultEncoder} from "../../../model/default-encoder";
import {DefaultPropertyType} from "../../../model/default-property-type";
import {DefaultMeasurementType} from "../../../model/default-measurement-type";
import {DefaultDecoder} from "../../../model/default-decoder";

@Component({
  selector: 'app-devices-form',
  templateUrl: './devices-form.component.html',
  styleUrls: ['./devices-form.component.scss']
})
export class DevicesFormComponent implements OnInit, ComponentCanDeactivate {

  @Input() active: boolean;
  @Output() activeChange = new EventEmitter();

  items: any[];
  currentStep: number;
  stepChanged: boolean;

  device: Device;
  template: Template;
  defaultDevice: DefaultDeviceCatalog;
  previousTemplateUuid: string;
  previousDefaultDeviceUuid: string;
  deviceMeasurements: DeviceMeasurement[];
  deviceExpressions: DeviceMeasurement[] = [];
  deviceProperties: DeviceProperty[];
  integrationModules: IntegrationModule[];
  asset: Asset;
  assetSearchData: AssetSearchData;
  persistingChanges: boolean;
  formLoaded: boolean;
  formSuccess: boolean;

  map: mapboxgl.Map;

  mode: 'create' | 'edit';

  @ViewChild(DevicesFormInfoComponent)
  devicesFormInfo: DevicesFormInfoComponent;

  constructor(private devicesService: DevicesService,
              private hwCatalogService: HwCatalogService,
              private templatesService: TemplatesService,
              private route: ActivatedRoute,
              private router: Router,
              private mapService: MapService,
              private commonMessagesService: CommonMessagesService) {
    this.device = new Device();
    this.deviceMeasurements = [];
    this.deviceProperties = [];
    this.integrationModules = [];
    this.asset = undefined;
    this.assetSearchData = undefined;
    this.formSuccess = false;
    this.stepChanged = false;
  }

  ngOnInit(): void {
    this.currentStep = 0;
    this.items = [
      {index: 0, icon: 'pi pi-mobile', label: 'model.device', description: 'forms.descriptions.device'},
      {index: 1, icon: 'pi pi-wifi', label: 'model.sensors', description: 'forms.descriptions.deviceSensors'},
      {index: 2, icon: 'pi pi-sliders-h', label: 'model.expressions', description: 'forms.descriptions.deviceExpressions'},
      {index: 3, icon: 'pi pi-bars', label: 'model.properties', description: 'forms.descriptions.deviceProperties'},
      {index: 4, icon: 'pi pi-table', label: 'model.integrationModules', description: 'forms.descriptions.integrationModules'},
      {index: 5, icon: 'pi pi-map-marker', label: 'model.asset', description: 'forms.descriptions.assetDevice'},
      {index: 6, icon: 'pi pi-check', label: 'forms.confirmation'}
    ];

    this.route.url.subscribe(urlSegment => {
      const firstPath = urlSegment[0].path;
      if (firstPath === 'new') {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(firstPath);
      }
    });
  }

  buildAndConfigureMap(): void {
    if (this.asset) {
      this.map = this.mapService.buildMap(14, this.asset.latitude, this.asset.longitude, 'deviceConfirmationMap');
      this.mapService.buildStaticPulsingDot(this.map, this.asset.latitude, this.asset.longitude);
    }
  }

  loadCreationForm(): void {
    this.device = new Device();
    this.formLoaded = true;
  }

  loadEditionForm(uuid): void {
    const promises = [
      this.devicesService.getDevice(uuid).toPromise()
        .then(response => {
          this.device = response.device;
          if (this.device.technicalDetails) {
            this.device.technicalDetails = JSON.parse(this.device.technicalDetails);
          }
        }, error => {
          if (error.status === HttpStatusCode.NotFound) {
            this.router.navigate(['/not-found']);
          }
        }),
      this.devicesService.getDeviceMeasurementsOfDevice(uuid).toPromise()
        .then(response => {
          this.deviceMeasurements = response.deviceMeasurements.filter(measurement => !measurement.expression);
          this.deviceExpressions = response.deviceMeasurements.filter(measurement => measurement.expression);
        }),
      this.devicesService.getDeviceProperties(uuid).toPromise()
        .then(response => this.deviceProperties = response.deviceProperties),
      this.devicesService.getDeviceAssets(uuid).toPromise().then(response => {
        if (response.assetDevices.length > 0) {
          this.asset = response.assetDevices[0].asset;
        }
      }),
      this.devicesService.getDeviceIntegrationModules(uuid).toPromise().then(response => {
        this.integrationModules = response.integrationModules;
        this.integrationModules.map(module => {
          module.technicalDetails = module.technicalDetails ? JSON.parse(module.technicalDetails) : module.technicalDetails;
          module.additionalInfo = module.additionalInfo;
          return module;
        });
      }),
    ];

    Promise.all(promises).then(() => this.formLoaded = true);
  }

  persistDeviceData(): void {
    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    this.integrationModules.forEach(module => {
      return module.technicalDetails = module.technicalDetails ? JSON.stringify(module.technicalDetails) : null;
    });

    let promise;
    if (this.mode === 'create') {
      promise = this.devicesService.createDevice(persistData).toPromise();
    } else {
      promise = this.devicesService.editDevice(persistData).toPromise();
    }

    promise.then(response => {
      if (this.active) {
        this.active = false;
        this.activeChange.emit(this.active);
      }
      this.formSuccess = true;
      this.router.navigate(([`/devices/${this.mode === 'create' ? response.uuid : persistData.uuid}/details`]));
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.serialNumber);
      this.persistingChanges = false;
    });
  }

  onNextStep(): void {
    this.stepChanged = true;
    if (this.currentStep === 0 && this.template && (!this.previousTemplateUuid || this.previousTemplateUuid != this.template.uuid)) {
      this.templateChanged(this.template);
    } else if (this.currentStep === 0 && this.defaultDevice && (!this.previousDefaultDeviceUuid || this.previousDefaultDeviceUuid != this.defaultDevice.uuid)) {
      this.defaultDeviceChanged(this.defaultDevice);
    } else {
      if (this.currentStep < this.items.length - 1) {
        this.currentStep++;
      }
      if (this.currentStep === this.items.length - 1) {
        setTimeout(() => this.buildAndConfigureMap(), 200);
      }
    }
  }

  onPreviousStep(): void {
    this.stepChanged = true;
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  tenantChanged(): void {
    this.deviceMeasurements = [];
    this.deviceProperties = [];
    this.integrationModules = [];
    this.asset = undefined;
    this.assetSearchData = undefined;
  }

  organizationChanged(): void {
    this.asset = undefined;
    this.assetSearchData = undefined;
  }

  templateChanged(template: Template): void {
    this.templatesService.getTemplate(template.uuid).toPromise().then(response => {
      let fullTemplate = response.template;
      if (fullTemplate.content?.deviceMeasurements) {
        let tempDeviceMeasurements = fullTemplate.content.deviceMeasurements.filter(measurement => !measurement.expression);
        this.deviceMeasurements.push.apply(this.deviceMeasurements, tempDeviceMeasurements);
        let tempDeviceExpressions = fullTemplate.content.deviceMeasurements.filter(measurement => measurement.expression);
        this.deviceExpressions.push.apply(this.deviceExpressions, tempDeviceExpressions);
      }
      if (fullTemplate.content?.deviceProperties) {
        this.deviceProperties.push.apply(this.deviceProperties, fullTemplate.content.deviceProperties);
      }
      if (fullTemplate.content?.integrationModules) {
        this.integrationModules.push.apply(this.integrationModules, fullTemplate.content.integrationModules);
      }
      this.previousTemplateUuid = this.template.uuid;
      this.currentStep++;
    }, error => {
      if (error.status === 404) {
        this.router.navigate(['/not-found']);
      }
    });
  }

  defaultDeviceChanged(defaultDevice: DefaultDeviceCatalog): void {
    this.hwCatalogService.getDefaultDeviceCatalog(defaultDevice.uuid).toPromise().then(response => {
      let fullDefaultDevice = response.defaultDeviceCatalog;
      if (fullDefaultDevice.content?.deviceMeasurements) {
        let tempDeviceMeasurements = fullDefaultDevice.content.deviceMeasurements.filter(measurement => {
          measurement.measurementType = new DefaultMeasurementType(measurement.measurementType.sensor,
            measurement.measurementType.unit, measurement.measurementType.dataType);
          return !measurement.expression;
        });
        this.deviceMeasurements.push.apply(this.deviceMeasurements, tempDeviceMeasurements);
        let tempDeviceExpressions = fullDefaultDevice.content.deviceMeasurements.filter(measurement => {
          measurement.measurementType = new DefaultMeasurementType(measurement.measurementType.sensor,
            measurement.measurementType.unit, measurement.measurementType.dataType);
          return measurement.expression;
        });
        this.deviceExpressions.push.apply(this.deviceExpressions, tempDeviceExpressions);
      }
      if (fullDefaultDevice.content?.deviceProperties) {
        let tempDeviceProperties = fullDefaultDevice.content.deviceProperties.map(property => {
          property.propertyType = new DefaultPropertyType(property.propertyType.name, property.propertyType.unit,
            property.propertyType.dataType);
          return property;
        });
        this.deviceProperties.push.apply(this.deviceProperties, tempDeviceProperties);
      }
      if (fullDefaultDevice.content?.integrationModules) {
        let tempIntegrationModules = fullDefaultDevice.content.integrationModules.map(integrationModule => {
          if (integrationModule.decoder) {
            integrationModule.decoder = new DefaultDecoder(integrationModule.decoder.name, integrationModule.decoder.type,
              integrationModule.decoder.source, integrationModule.decoder.routingKey, integrationModule.decoder.creationDate,
              integrationModule.decoder.updatedDate);
          }
          if (integrationModule.encoder) {
            integrationModule.encoder = new DefaultEncoder(integrationModule.encoder.uuid, integrationModule.encoder.name,
              integrationModule.encoder.commandsSchema, integrationModule.encoder.creationDate, integrationModule.encoder.updatedDate);
          }
          return integrationModule;
        });
        this.integrationModules.push.apply(this.integrationModules, tempIntegrationModules);
      }
      this.previousDefaultDeviceUuid = this.defaultDevice.uuid;
      this.currentStep++;
    }, error => {
      if (error.status === 404) {
        this.router.navigate(['/not-found']);
      }
    });
  }

  private buildPersistData(): DevicePersistRequest {
    const measurements = this.deviceMeasurements.concat(this.deviceExpressions);
    return {
      serialNumber: this.device.serialNumber,
      description: this.device.description,
      alias: this.device.alias,
      timezone: this.device.timezone,
      technicalDetails: !this.device.technicalDetails ? null : JSON.stringify(this.device.technicalDetails),
      tenantUuid: this.device.tenant.uuid,
      organizationUuid: this.device.organization.uuid,
      deviceCategory: this.device.deviceCategory,
      deviceMeasurements: measurements,
      deviceProperties: this.deviceProperties,
      integrationModules: this.integrationModules,
      assetUuid: this.asset ? this.asset.uuid : null,
      uuid: this.mode === 'edit' ? this.device.uuid : null
    };
  }

  onCancel(route: string): Promise<boolean> {
    if (route == null) {
      return this.router.navigate([`/devices`]);
    }
    return this.router.navigate([`${route}`]);
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    if (this.formSuccess) {
      return true;
    }
    return !this.stepChanged && (this.currentStep === 0 && this.devicesFormInfo.deviceForm.untouched);
  }

}
